// BBB-424 prototype: combined layer + legend panel for the production map (ZonesScreen).
// Additive only — does not touch zone/plan rendering. Drives the BBB-423 engine
// (useLayerController) over the live map instance. A single hamburger control on the
// left opens one panel holding the 5 category toggles with the legend auto-appearing
// below for active layers. Region is automatic.
import { Fragment, useEffect, useMemo, useState } from 'react';
import styles from './MapLayerPanel.module.css';
import { CATALOGUE, getActiveSources, isRenderable } from './data/catalogue';
import { useLayerController } from './lib/useLayerController';
import { useVectorLayerController } from './lib/useVectorLayerController';
import { useLegend } from './lib/useLegend';
import { useMapCredits } from './lib/useMapCredits';
import { useZoneSoilLegend } from './lib/useZoneSoilLegend';
import { useMapLayers } from './lib/useMapLayers';
import { useMapZoom } from './lib/useMapZoom';
import { COVERED_REGIONS, isOutOfCoverage, useViewportRegion } from './lib/useViewportRegion';
import { isProblemStatus, layerStatusLine } from './lib/layerStatusCopy';
import { allZonesPoints, framePoints, zonePoints } from './lib/zoneBounds';
import { swatchStyle } from './lib/swatchStyle';
import helpers from '../../helpers';

// Google Maps mapTypeId values behind the Satellite/Street toggle.
const BASEMAP = { SATELLITE: 'hybrid', STREET: 'roadmap' };

// Category toggles (Soil type, Land classification / Land capability, Flood risk,
// Protected sites, Nitrate and water zones). The catalogue itself defines the
// kept set and the per-region labels.
const CATEGORY_TOGGLES = CATALOGUE;

export default function MapLayerPanel({
  map,
  onActiveLayersChange,
  onCreditsChange,
  zones,
  selectedZoneIndex,
}) {
  const { layerStates, toggle, setStatus } = useMapLayers();
  const viewportRegion = useViewportRegion(map);
  const activeRegion = COVERED_REGIONS.includes(viewportRegion) ? viewportRegion : null;
  const outOfCoverage = isOutOfCoverage(viewportRegion);
  const zoom = useMapZoom(map);

  // The soil legend lists the soil types in the OPEN field rather than the
  // provider's whole national key, so it needs the zone's land data. Fetched
  // only while the soil layer is on: see lib/useZoneSoilLegend.js.
  const openZone =
    selectedZoneIndex != null ? zones?.[selectedZoneIndex] ?? null : null;
  const { data: zoneSoilData, status: zoneSoilStatus } = useZoneSoilLegend(
    openZone,
    Boolean(layerStates.soil?.visible)
  );
  const legendItems = useLegend(
    layerStates,
    viewportRegion,
    undefined,
    zoneSoilData,
    zoneSoilStatus
  );
  // The credits the licences oblige us to show for what is on screen. See
  // data/attributions.js: the map had no attribution surface at all before
  // this, and two conditional-licence obligations were unmet the day it went
  // to production.
  //
  // Derived here because layerStates lives here, but rendered by ZoneDetails at
  // the foot of Land characteristics (Davin, 11/09/2026): even capped at 38% of
  // a 232px panel, the block was taking more room than the legend a farmer
  // opened it to read. Reported up the same way activeCount is.
  const credits = useMapCredits(layerStates, viewportRegion);

  // Recentre the map back onto the user's zones when the viewport drifts out of
  // coverage. Fit to the selected zone if one is open, otherwise to every drawn
  // zone. Points are derived by the pure zoneBounds helpers; we only need
  // google.maps here to turn them into a LatLngBounds to fitBounds.
  const returnPoints = useMemo(() => {
    const selectedZone =
      selectedZoneIndex != null ? zones?.[selectedZoneIndex] : null;
    return selectedZone ? zonePoints(selectedZone) : allZonesPoints(zones);
  }, [zones, selectedZoneIndex]);
  const canReturnToZone = returnPoints.length > 0;

  const returnToZone = () => {
    if (!map || !window.google?.maps) return;
    const frame = framePoints(returnPoints);
    if (!frame) return;
    // Single point (a centre-point-only zone): recentre at a fixed zoom, since
    // fitBounds on a zero-size bounds would slam to max zoom. Otherwise fit all.
    if (frame.mode === 'center') {
      map.setCenter(frame.center);
      map.setZoom(frame.zoom);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    frame.points.forEach((point) => bounds.extend(point));
    map.fitBounds(bounds);
  };

  useLayerController({ map, layerStates, viewportRegion, zoom, setStatus });
  // England designations / NVZ / safeguard zones draw as client-side vector
  // outlines, so they are reconciled here rather than as tile overlays (BBB-457).
  useVectorLayerController({ map, layerStates, viewportRegion, zoom, setStatus });

  const [panelOpen, setPanelOpen] = useState(false);
  const [basemap, setBasemap] = useState(BASEMAP.SATELLITE);

  // The panel owns the basemap so the satellite/street toggle survives ZonesScreen
  // re-renders (ZonesScreen no longer hardcodes mapTypeId in its map options).
  useEffect(() => {
    if (map) map.setMapTypeId(basemap);
  }, [map, basemap]);

  const activeCount = CATEGORY_TOGGLES.filter(
    (category) => layerStates[category.id]?.visible,
  ).length;

  // Let the parent (ZonesScreen) know when any layer is active, so the zone
  // polygon fill can step aside (outline-only) and stop hiding the layer beneath.
  useEffect(() => {
    if (onActiveLayersChange) onActiveLayersChange(activeCount);
  }, [activeCount, onActiveLayersChange]);

  useEffect(() => {
    if (onCreditsChange) onCreditsChange(credits);
  }, [credits, onCreditsChange]);

  const hasLegend = legendItems.length > 0;

  return (
    <>
      <div className={styles.basemapToggle}>
        <button
          type="button"
          className={basemap === BASEMAP.SATELLITE ? styles.basemapBtnActive : styles.basemapBtn}
          onClick={() => setBasemap(BASEMAP.SATELLITE)}
        >
          Satellite
        </button>
        <button
          type="button"
          className={basemap === BASEMAP.STREET ? styles.basemapBtnActive : styles.basemapBtn}
          onClick={() => setBasemap(BASEMAP.STREET)}
        >
          Street
        </button>
      </div>

      {/* Closed state: a single hamburger button. The green badge shows how many
          layers are active so the count survives the panel being shut. */}
      <button
        type="button"
        className={helpers.clx(styles.menuFab, panelOpen && styles.menuFabHidden)}
        onClick={() => setPanelOpen(true)}
        aria-label="Open map layers"
        aria-expanded={panelOpen}
      >
        <span className={styles.burger}>
          <span />
          <span />
          <span />
        </span>
        {activeCount > 0 && <span className={styles.fabBadge}>{activeCount}</span>}
      </button>

      {/* Open state: one panel with toggles on top, legend auto-appended below. */}
      <div
        className={helpers.clx(styles.panel, panelOpen ? styles.panelOpen : styles.panelClosed)}
        aria-hidden={!panelOpen}
      >
        <button
          type="button"
          className={styles.closeBtn}
          onClick={() => setPanelOpen(false)}
          aria-label="Close map layers"
          tabIndex={panelOpen ? 0 : -1}
        >
          <span className={styles.closeIcon} />
        </button>

        {/* Out of coverage: the panel morphs into a single "return to zone"
            action — same panel, same close (X). Layers/legend are useless
            off-map, so we offer a jump back to the user's zones instead: the
            selected zone if one is open, otherwise every drawn zone. */}
        {outOfCoverage ? (
          <div className={styles.returnPanel}>
            <div className={styles.returnTitle}>You&rsquo;ve moved off the map</div>
            <div className={styles.returnSubtitle}>Jump back to:</div>
            <div className={styles.returnButtons}>
              <button
                type="button"
                className={styles.returnBtn}
                onClick={returnToZone}
                disabled={!canReturnToZone}
                tabIndex={panelOpen ? 0 : -1}
              >
                Return to zone
              </button>
            </div>
          </div>
        ) : (
          <>
        {/* Toggles stay pinned (never scroll) so a layer can always be turned off,
            even when its legend is long. Only the legend list scrolls. */}
        <div className={styles.toggleList}>
          {CATEGORY_TOGGLES.map((category) => {
            const isOn = !!layerStates[category.id]?.visible;
            const status = layerStates[category.id]?.status;
            const statusLine = layerStatusLine(status);
            const availableHere =
              !activeRegion ||
              getActiveSources(category, activeRegion).filter(isRenderable).length > 0;
            // No layer for this region: hide the toggle entirely — unless it's
            // already on, in which case keep it visible but greyed + locked so it
            // restores when the map returns to a covered region (e.g. Nitrate
            // zones has no Wales layer).
            if (!availableHere && !isOn) return null;
            const locked = !availableHere && isOn;
            // Land capability uses a region-specific name (ALC vs LCA).
            const toggleLabel =
              (activeRegion && category.regionLabels?.[activeRegion]) || category.label;
            return (
              <button
                key={category.id}
                type="button"
                className={helpers.clx(
                  isOn ? styles.toggleRowActive : styles.toggleRow,
                  locked && styles.toggleRowLocked,
                )}
                onClick={() => {
                  if (!locked) toggle(category.id);
                }}
                disabled={locked}
                title={locked ? 'Not available in this area' : undefined}
                tabIndex={panelOpen && !locked ? 0 : -1}
              >
                <span className={styles.toggleText}>
                  <span className={styles.toggleLabel}>{toggleLabel}</span>
                  {/* Health comes from real tile outcomes (lib/layerHealth.js), so
                      this only ever appears once the layer has actually asked a
                      server for something. Wording lives in lib/layerStatusCopy.js. */}
                  {isOn && statusLine && (
                    <span
                      className={helpers.clx(
                        styles.toggleStatus,
                        isProblemStatus(status) && styles.toggleStatusProblem,
                      )}
                    >
                      {statusLine}
                    </span>
                  )}
                </span>
                <span className={isOn ? styles.switchOn : styles.switch}>
                  <span className={styles.switchKnob} />
                </span>
              </button>
            );
          })}
        </div>

        {hasLegend && (
          <div className={styles.legendSection}>
            <div className={styles.legendDivider} />
            {/* The "Legend" heading was removed on 11/09/2026, Davin's call: a
                panel of swatches under the layer toggles does not need naming,
                and the word was the only ALL CAPS string on the surface. */}
            <div className={styles.legendList}>
              {legendItems.map((item) => {
                const swatches = item.swatches || [];
                // A single-swatch item is ONE thing, so it gets one row: the
                // swatch beside the item's own label. It used to render the
                // label as a heading and then the swatch with its own label
                // underneath, which for most layers said the same thing twice
                // ("Nitrate zones", then a swatch captioned "Nitrate Vulnerable
                // Zone"). Davin, 18/08: "dumb".
                //
                // Multi-swatch items keep the heading, because there the heading
                // names the layer and each row names a class within it, which is
                // the case the two-level layout was actually built for (Wales
                // flood: High / Medium / Low).
                //
                // Soil opts out via `alwaysLabelSwatches`: a field with one soil
                // type would otherwise collapse to a swatch captioned "Soil
                // type", throwing away the only thing the card is there to say.
                const single = swatches.length === 1 && !item.alwaysLabelSwatches;

                return (
                  <div key={item.sourceId} className={styles.legendItem}>
                    {!single && !item.hideLabel && (
                      <div className={styles.legendItemLabel}>{item.label}</div>
                    )}
                    {swatches.map((swatch, i) => (
                      <Fragment key={i}>
                        {/* Soil scans a little beyond the boundary (BBB-427).
                            Those classes are broken off under their own heading,
                            because a farmer must never read a soil type here as
                            being in their field when it is up to 250 m away. */}
                        {/* The "Around the field" heading that used to open the
                            nearby group was removed on 11/09/2026. The nearby
                            rows keep their own styling below, which is now the
                            only thing marking them as outside the zone. */}
                        <div
                          className={`${styles.swatchRow} ${
                            swatch.nearby ? styles.swatchRowNearby : ''
                          }`}
                        >
                          <span
                            className={styles.swatch}
                            style={swatchStyle(swatch)}
                          />
                          <span className={styles.swatchLabel}>
                            {single ? item.label : swatch.label}
                          </span>
                        </div>
                      </Fragment>
                    ))}
                    {item.caption && (
                      <div className={styles.legendCaption}>{item.caption}</div>
                    )}
                    {item.link && (
                      <a
                        className={styles.legendLink}
                        href={item.link.href}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {item.link.text}
                      </a>
                    )}
                    {/* Source links for every other category. `item.link` above
                        is soil's, which buildSoilLegend makes itself; these come
                        from the catalogue's own `sourceLink` fields, which were
                        in the bundle and unrendered until now. A merged card can
                        carry more than one. */}
                    {item.sourceLinks?.map((sourceLink) => (
                      <a
                        key={sourceLink.href}
                        className={styles.legendLink}
                        href={sourceLink.href}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {sourceLink.text}
                      </a>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}
          </>
        )}
      </div>
    </>
  );
}
